import React from 'react';
import { Box, Checkbox, FormControlLabel, FormGroup, Typography, Link, Tooltip } from '@mui/material';
import { LEGAL_LINKS } from '@/app/constants/legalLinks';

export interface ConsentState {
    agbAccepted: boolean;
    datenschutzAccepted: boolean;
}

interface ConsentSectionProps {
    consents: ConsentState;
    onConsentChange: (consents: ConsentState) => void;
    disabled?: boolean;
    errors?: {
        agb?: string;
        datenschutz?: string;
    };
}

const ConsentSection: React.FC<ConsentSectionProps> = ({
    consents,
    onConsentChange,
    disabled = false,
    errors = {}
}) => {
    const handleAGBChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onConsentChange({
            ...consents,
            agbAccepted: event.target.checked
        });
    };

    const handleDatenschutzChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onConsentChange({
            ...consents,
            datenschutzAccepted: event.target.checked
        });
    };

    return (
        <Box sx={{ 
            mt: 3,
            p: 2.5,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: errors.agb || errors.datenschutz ? 'error.main' : 'grey.200'
        }}>
            <Typography 
                variant="subtitle2" 
                color="text.primary" 
                sx={{ 
                    mb: 2,
                    fontWeight: 600,
                    fontSize: '0.875rem'
                }}
            >
                Zustimmung erforderlich:
            </Typography>

            <FormGroup>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* AGB Checkbox */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={consents.agbAccepted}
                                onChange={handleAGBChange}
                                disabled={disabled}
                                size="small"
                                sx={{ 
                                    alignSelf: 'flex-start',
                                    mt: 0,
                                    '& .MuiSvgIcon-root': { fontSize: 18 }
                                }}
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                                    Ich akzeptiere die
                                </Typography>
                                <Tooltip title="Allgemeine Geschäftsbedingungen" arrow>
                                    <Link 
                                        href={LEGAL_LINKS.AGB_URL} 
                                        target="_blank" 
                                        sx={{ 
                                            fontSize: '0.8rem',
                                            textDecoration: 'underline',
                                            color: 'primary.main',
                                            ml: "0 !important"
                                        }}
                                    >
                                        AGB
                                    </Link>
                                </Tooltip>
                            </Box>
                        }
                        sx={{ 
                            alignItems: 'center',
                            ml: 0,
                            color: errors.agb ? 'error.main' : 'inherit'
                        }}
                    />

                    {errors.agb && (
                        <Typography 
                            variant="caption" 
                            color="error" 
                            sx={{ 
                                ml: 4,
                                mt: -1,
                                fontSize: '0.75rem'
                            }}
                        >
                            {errors.agb}
                        </Typography>
                    )}

                    {/* Datenschutz Checkbox */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={consents.datenschutzAccepted}
                                onChange={handleDatenschutzChange}
                                disabled={disabled}
                                size="small"
                                sx={{ 
                                    alignSelf: 'flex-start',
                                    mt: 0,
                                    '& .MuiSvgIcon-root': { fontSize: 18 }
                                }}
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                                    Ich akzeptiere die
                                </Typography>
                                <Link 
                                    href={LEGAL_LINKS.PRIVACY_URL} 
                                    target="_blank" 
                                    sx={{ 
                                        fontSize: '0.8rem',
                                        textDecoration: 'underline',
                                        color: 'primary.main',
                                        ml: "0 !important"
                                    }}
                                >
                                    Datenschutzerklärung
                                </Link>
                            </Box>
                        }
                        sx={{ 
                            alignItems: 'center',
                            ml: 0,
                            color: errors.datenschutz ? 'error.main' : 'inherit'
                        }}
                    />

                    {errors.datenschutz && (
                        <Typography 
                            variant="caption" 
                            color="error" 
                            sx={{ 
                                ml: 4,
                                mt: -1,
                                fontSize: '0.75rem'
                            }}
                        >
                            {errors.datenschutz}
                        </Typography>
                    )}
                </Box>
            </FormGroup>
        </Box>
    );
};

export default ConsentSection; 